import type { Branded } from './branded'
import type { ClientId } from './client'
import type { UserId } from './project'

type DomainId = Branded<string, 'DomainId'>

const domainId = (value: string): DomainId => value as DomainId

const ROLE = {
	OWNER: 'owner',
	EDITOR: 'editor',
	VIEWER: 'viewer',
} as const

const ROLES = [ROLE.OWNER, ROLE.EDITOR, ROLE.VIEWER] as const

type Role = (typeof ROLES)[number]

type Member = {
	readonly userId: UserId
	readonly role: Role
	readonly email: string
	readonly name: string
}

// Membership hangs off the domain, not the project: one roster covers every
// project a client's domain holds, which is what the PocketBase rules check.
type Domain = {
	readonly id: DomainId
	readonly clientId: ClientId
	readonly slug: string
	readonly name: string
	readonly descr: string
	readonly members: readonly Member[]
	readonly createdAt: Date
	readonly updatedAt: Date
}

const canWrite = (role: Role): boolean => role === ROLE.OWNER || role === ROLE.EDITOR

const canAdmin = (role: Role): boolean => role === ROLE.OWNER

const roleOf = (domain: Domain, user: UserId): Role | undefined =>
	domain.members.find(member => member.userId === user)?.role

const ownersOf = (domain: Domain): readonly Member[] => domain.members.filter(member => member.role === ROLE.OWNER)

export { canAdmin, canWrite, domainId, ownersOf, ROLE, roleOf, ROLES }
export type { Domain, DomainId, Member, Role }
